import type { NonSerializableStateStore, ReplicationTarget, Serializable, StateStore } from '@playcastdotio/CoreTypes';
import { generators, PlaycastMessage, PlaycastMessageStateReplication } from '@playcastdotio/Messaging';

const DEBUG = false;

const replicationStateChangeCallbacks: {
  [key in ReplicationTarget]: Record<
    string,
    (message: PlaycastMessage<PlaycastMessageStateReplication<string>>, splitReplicationKey?: string) => void
  >;
} = {} as Record<
  ReplicationTarget,
  Record<string, (message: PlaycastMessage<PlaycastMessageStateReplication<string>>, splitReplicationKey?: string) => void>
>;

const allReplicatedStateStores: Record<
  ReplicationTarget,
  {
    channel: string;
    store: StateStore<Serializable>;
  }[]
> = {} as Record<ReplicationTarget, { channel: string; store: StateStore<Serializable> }[]>;

export type StateStoreCreationConfig<T> = {
  replicate?: { channel: string; targets: ReplicationTarget[]; rateLimit?: number };
  splitReplicate?: { channel: string; targets: ReplicationTarget[]; splitKey?: string[] };
  transformFunction?: (oldState: T, incomingState: T) => T;
};

export const makeStateStore = <T extends Serializable>(initialState: T, config?: StateStoreCreationConfig<T>): StateStore<T> => {
  const stateStore: StateStore<T> = {
    currentState: structuredClone(initialState),
    listeners: {},
    addListener: (cb) => {
      const listenerId = Math.random().toString();
      stateStore.listeners[listenerId] = cb;
      return listenerId;
    },
    removeListener: (id) => {
      if (id) delete stateStore.listeners[id];
      if (!id) stateStore.listeners = {};
    },
    updateState: (state) => {
      const newState = config?.transformFunction
        ? config.transformFunction(structuredClone(stateStore.currentState), structuredClone(state))
        : structuredClone(state);
      const previousState = structuredClone(stateStore.currentState);
      stateStore.currentState = newState;
      Object.values(stateStore.listeners).forEach((cb) => cb(newState, previousState));
    },
    reset: (destroyListeners?: boolean) => {
      stateStore.currentState = structuredClone(initialState);
      if (destroyListeners) {
        stateStore.listeners = {};
        addReplicators();
      }
    },
  };
  const addReplicators = () => {
    if (config?.replicate) {
      const channel = config.replicate.channel;
      config.replicate.targets.forEach((target) => {
        let lastUpdate = 0;
        let pendingUpdate: T | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const sendUpdate = (state: T) => {
          if (DEBUG) console.log('[StateStore] Replication for', channel, 'to', target, state);
          const message = generators.state.replication(channel, structuredClone(state));
          if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
          Object.values(replicationStateChangeCallbacks[target]).forEach((cb) => cb(message));
          lastUpdate = Date.now();
          pendingUpdate = null;
        };

        stateStore.addListener((state) => {
          if (config.replicate?.rateLimit !== undefined) {
            const now = Date.now();
            if (now - lastUpdate < config.replicate.rateLimit) {
              pendingUpdate = state;
              if (!timeoutId) {
                timeoutId = setTimeout(
                  () => {
                    if (pendingUpdate !== null) {
                      sendUpdate(pendingUpdate);
                    }
                    timeoutId = null;
                  },
                  config.replicate.rateLimit - (now - lastUpdate),
                );
              }
            } else {
              sendUpdate(state);
            }
          } else {
            sendUpdate(state);
          }
        });

        const channelObject = allReplicatedStateStores[target];
        if (!channelObject) allReplicatedStateStores[target] = [];
        allReplicatedStateStores[target].push({
          channel,
          store: stateStore as unknown as StateStore<Serializable>,
        });
      });
    }
    if (config?.splitReplicate) {
      const channel = config.splitReplicate.channel;
      config.splitReplicate.targets.forEach((target) => {
        stateStore.addListener((state, previousState) => {
          const splitReplicate = config.splitReplicate;
          if (!splitReplicate) return;

          const splitKey = splitReplicate.splitKey || [];

          let nestedState: Serializable = state;
          let nestedRecord: { [key: string]: Serializable } = {};

          if (splitKey.length === 0) {
            if (typeof state === 'object' && !Array.isArray(state) && state) {
              nestedRecord = structuredClone(state) as { [key: string]: Serializable };
            }
          }

          for (let i = 0; i < splitKey.length; i++) {
            if (typeof nestedState !== 'object' || !nestedState || Array.isArray(nestedState)) return;

            nestedRecord = structuredClone(nestedState) as { [key: string]: Serializable };
            nestedState = (nestedState as { [key: string]: Serializable })[splitKey[i]];

            if (nestedState === undefined) {
              console.error('splitKey does not point to an object');
              return;
            }

            if (splitKey.length - 1 === i) {
              const maybeResult = nestedState;
              if (typeof maybeResult === 'object' && !Array.isArray(maybeResult) && maybeResult) {
                nestedRecord = structuredClone(maybeResult) as { [key: string]: Serializable };
                break;
              }
              console.error('splitKey does not point to an object');
            }
          }

          if (!nestedRecord) {
            console.error('splitKey does not point to an object');
            return;
          }

          const splitReplicationKey = Object.keys(nestedRecord);

          splitReplicationKey.forEach((key) => {
            const newState = nestedRecord[key];
            if (DEBUG) console.log('[StateStore] Split replication', key, newState);

            const cachedValue =
              typeof previousState === 'object' && previousState && !Array.isArray(previousState)
                ? (previousState as { [key: string]: Serializable })[key]
                : undefined;
            if (JSON.stringify(newState) !== JSON.stringify(cachedValue)) {
              const message = generators.state.replication(channel, structuredClone(newState));
              if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
              Object.values(replicationStateChangeCallbacks[target]).forEach((cb) => cb(message, key));
            }
          });
        });
      });
    }
  };
  addReplicators();
  return stateStore;
};

export const makeNonSerializableStateStore = <T>(initialState: T): NonSerializableStateStore<T> => {
  const stateStore: NonSerializableStateStore<T> = {
    currentState: initialState,
    listeners: {},
    addListener: (cb) => {
      const listenerId = Math.random().toString();
      stateStore.listeners[listenerId] = cb;
      return listenerId;
    },
    removeListener: (id) => {
      if (id) delete stateStore.listeners[id];
      if (!id) stateStore.listeners = {};
    },
    updateState: (state) => {
      stateStore.currentState = state;
      Object.values(stateStore.listeners).forEach((cb) => cb(state));
    },
    reset: (destroyListeners?: boolean) => {
      stateStore.currentState = initialState;
      if (destroyListeners) {
        stateStore.listeners = {};
      }
    },
  };
  return stateStore;
};

export const addReplicationStateChangeCallback = (
  target: ReplicationTarget,
  callback: (message: PlaycastMessage<PlaycastMessageStateReplication<string>>, splitReplicationKey?: string) => void,
): string => {
  const listenerId = Math.random().toString();
  if (!replicationStateChangeCallbacks[target]) replicationStateChangeCallbacks[target] = {};
  replicationStateChangeCallbacks[target][listenerId] = callback;
  return listenerId;
};

export const removeReplicationStateChangeCallback = (target: ReplicationTarget, listenerId: string): void => {
  delete replicationStateChangeCallbacks[target][listenerId];
};

export const removeAllReplicationStateChangeCallbacks = (target?: ReplicationTarget): void => {
  if (target) {
    replicationStateChangeCallbacks[target] = {};
  } else {
    Object.values(replicationStateChangeCallbacks).forEach((target) => {
      target = {};
    });
  }
};

export const getNotifyMessages = (targets: ReplicationTarget[]) => {
  const output: Record<ReplicationTarget, PlaycastMessage<PlaycastMessageStateReplication<string>>[]> = {} as any;
  targets.forEach((target) => {
    if (!output[target]) output[target] = [];
    const channels = allReplicatedStateStores[target];
    if (channels) {
      channels.forEach((channel) => {
        if (DEBUG) console.log('[StateStore] Notify', target, channel.channel, channel.store.currentState);
        output[target].push(generators.state.replication(channel.channel, channel.store.currentState));
      });
    }
  });
  return output;
};
